// API utility for admin endpoints
const API_URL = import.meta.env.VITE_API_URL ? 
  `${import.meta.env.VITE_API_URL}/api` : 
  'http://localhost:4000/api';

console.log('API Base URL:', API_URL); // Debug log

export default API_URL;

// Helper function to get auth headers
const getAuthHeaders = (token) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Helper function to handle fetch requests
const fetchWithAuth = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    mode: 'cors',
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('API Error:', {
      status: response.status,
      statusText: response.statusText,
      url,
      error: errorText
    });
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }
  
  return response;
};

export async function login(username, password) {
  try {
    console.log('Attempting login with username:', username);
    const res = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ username, password }),
      credentials: 'include',
      mode: 'cors'
    });
    
    const data = await res.json().catch(() => ({}));
    
    if (!res.ok) {
      console.error('Login failed:', { status: res.status, data });
      throw new Error(data.error || 'Login failed: Invalid credentials');
    }
    
    if (!data.token) {
      throw new Error('No token received from server');
    }
    
    console.log('Login successful, token received');
    return data;
  } catch (error) {
    console.error('Error in login:', error);
    throw error;
  }
}

export async function getDirectors(token) {
  console.log('Fetching directors...');
  
  try {
    const res = await fetchWithAuth(`${API_URL}/directors`, { 
      method: 'GET',
      headers: getAuthHeaders(token)
    });
    
    return await res.json();
  } catch (error) {
    console.error('Error in getDirectors:', error);
    throw error;
  }
}

export async function createDirector(data, token) {
  // Ensure active is a boolean
  const directorData = {
    ...data,
    active: data.active === true || data.active === 'true' || data.active === 1
  };
  
  console.log('Creating director with data:', directorData);
  console.log('Using token:', token ? 'Token present' : 'No token');
  
  try {
    const res = await fetch(`${API_URL}/directors`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(directorData),
      credentials: 'include',
      mode: 'cors'
    });
    
    const responseData = await res.json().catch(() => ({}));
    
    if (!res.ok) {
      console.error('Error response from server:', { status: res.status, data: responseData });
      throw new Error(responseData.error || `Failed to create director: ${res.status} ${res.statusText}`);
    }
    
    return responseData;
  } catch (error) {
    console.error('Error in createDirector:', error);
    throw error;
  }
}

export async function updateDirector(id, data, token) {
  // Ensure active is a boolean
  const directorData = {
    ...data,
    active: data.active === true || data.active === 'true' || data.active === 1
  };
  
  console.log(`Updating director ${id} with data:`, directorData);
  
  try {
    const res = await fetch(`${API_URL}/directors/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      body: JSON.stringify(directorData),
      credentials: 'include',
      mode: 'cors'
    });
    
    const responseData = await res.json().catch(() => ({}));
    
    if (!res.ok) {
      console.error('Error response from server:', { status: res.status, data: responseData });
      throw new Error(responseData.error || `Failed to update director: ${res.status} ${res.statusText}`);
    }
    
    return responseData;
  } catch (error) {
    console.error('Error in updateDirector:', error);
    throw error;
  }
}

export async function deleteDirector(id, token) {
  const res = await fetch(`${API_URL}/directors/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to delete director');
  return res.json();
}

// Videos CRUD
export async function getVideos() {
  const res = await fetch(`${API_URL}/videos`);
  if (!res.ok) throw new Error('Failed to fetch videos');
  return res.json();
}

export async function createVideo(data, token) {
  console.log('Creating video with data:', data);
  try {
    const res = await fetch(`${API_URL}/videos`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify(data)
    });
    
    const responseData = await res.json();
    console.log('Create video response:', { status: res.status, data: responseData });
    
    if (!res.ok) {
      throw new Error(responseData.error || 'Failed to create video');
    }
    
    return responseData;
  } catch (error) {
    console.error('Error in createVideo:', error);
    throw error;
  }
}

export async function updateVideo(id, data, token) {
  console.log(`Updating video ${id} with data:`, data);
  try {
    const res = await fetch(`${API_URL}/videos/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json', 
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify(data)
    });
    
    const responseData = await res.json();
    console.log('Update video response:', { status: res.status, data: responseData });
    
    if (!res.ok) {
      throw new Error(responseData.error || 'Failed to update video');
    }
    
    return responseData;
  } catch (error) {
    console.error('Error in updateVideo:', error);
    throw error;
  }
}

export async function deleteVideo(id, token) {
  const res = await fetch(`${API_URL}/videos/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to delete video');
  return res.json();
}

export async function getDirectorOrderedVideos(directorId, token) {
  if (!directorId) throw new Error('directorId is required to fetch director-specific videos');
  const res = await fetch(`${API_URL}/videos?director_id=${directorId}`, {
    method: 'GET',
    headers: getAuthHeaders(token)
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to fetch director ordered videos' }));
    throw new Error(errorData.error || `Failed to fetch director ordered videos: ${res.status}`);
  }
  return res.json();
}

export async function updateDirectorVideoOrder(dvoId, payload, token) {
  if (!dvoId) throw new Error('dvoId is required to update director video order');
  if (!payload || typeof payload.new_display_order !== 'number') {
    throw new Error('Invalid payload: new_display_order (number) is required.');
  }
  const res = await fetch(`${API_URL}/director-video-order/${dvoId}`, {
    method: 'PUT',
    headers: getAuthHeaders(token),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to update director video order' }));
    throw new Error(errorData.error || `Failed to update director video order: ${res.status}`);
  }
  return res.json();
}

// Featured Spots CRUD
export async function getFeatured() {
  const res = await fetch(`${API_URL}/featured?order=display_order`);
  if (!res.ok) {
    const error = await res.text();
    console.error('Error response:', error);
    throw new Error(`Failed to fetch featured spots: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function createFeatured(data, token) {
  const res = await fetch(`${API_URL}/featured`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Failed to create featured spot');
  return res.json();
}

export async function updateFeatured(id, data, token) {
  const res = await fetch(`${API_URL}/featured/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Failed to update featured spot');
  return res.json();
}

export async function deleteFeatured(id, token) {
  const res = await fetch(`${API_URL}/featured/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) {
    const error = await res.text();
    throw new Error(`Failed to delete featured spot: ${error}`);
  }
  return res.json();
}
